import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import { Topbar } from '@/components/Topbar';
import { StatusPill } from '@/components/StatusPill';
import { Avatar } from '@/components/Avatar';
import { Icon } from '@/components/Icon';
import { haptic } from '@/lib/telegram';
import type { Account, CampaignAccountRole } from '@/types/domain';

type DraftEntry = {
  selected: boolean;
  role: CampaignAccountRole;
};

const ROLE_OPTIONS: Array<{ value: CampaignAccountRole; label: string }> = [
  { value: null, label: 'Любой' },
  { value: 'A', label: 'A' },
  { value: 'B', label: 'B' },
];

export function CampaignAccountsPage(): JSX.Element {
  const { id } = useParams();
  const cid = Number(id);
  const { data: accounts, loading: l1 } = useAsync(() => api.listAccounts(), []);
  const { data: attached, loading: l2, reload: reloadAttached } = useAsync(
    () => api.listAttachedAccounts(cid),
    [cid],
  );

  const [draft, setDraft] = useState<Record<number, DraftEntry>>({});
  const [saveBusy, setSaveBusy] = useState(false);

  const attachedById = useMemo(() => {
    const map = new Map<number, Account>();
    for (const account of attached ?? []) map.set(account.id, account);
    return map;
  }, [attached]);

  useEffect(() => {
    if (!accounts || !attached) return;
    const next: Record<number, DraftEntry> = {};
    for (const account of accounts) {
      const current = attachedById.get(account.id);
      next[account.id] = {
        selected: Boolean(current),
        role: current?.role === 'A' || current?.role === 'B' ? current.role : null,
      };
    }
    setDraft(next);
  }, [accounts, attached, attachedById]);

  const loading = l1 || l2;

  const selectedCount = useMemo(
    () => Object.values(draft).filter((item) => item.selected).length,
    [draft],
  );

  const hasChanges = useMemo(() => {
    if (!accounts) return false;
    return accounts.some((account) => {
      const initial = attachedById.get(account.id);
      const current = draft[account.id] ?? { selected: false, role: null };
      const initialSelected = Boolean(initial);
      const initialRole = initial?.role === 'A' || initial?.role === 'B' ? initial.role : null;
      return current.selected !== initialSelected || current.role !== initialRole;
    });
  }, [accounts, attachedById, draft]);

  const toggleSelection = (accountId: number) => {
    setDraft((current) => {
      const item = current[accountId] ?? { selected: false, role: null };
      return {
        ...current,
        [accountId]: {
          selected: !item.selected,
          role: item.role,
        },
      };
    });
  };

  const setRole = (accountId: number, role: CampaignAccountRole) => {
    setDraft((current) => {
      const item = current[accountId] ?? { selected: true, role: null };
      return {
        ...current,
        [accountId]: {
          selected: true,
          role,
        },
      };
    });
  };

  const resetDraft = () => {
    if (!accounts || !attached) return;
    const next: Record<number, DraftEntry> = {};
    for (const account of accounts) {
      const current = attachedById.get(account.id);
      next[account.id] = {
        selected: Boolean(current),
        role: current?.role === 'A' || current?.role === 'B' ? current.role : null,
      };
    }
    setDraft(next);
  };

  const save = async () => {
    if (saveBusy || !accounts) return;
    setSaveBusy(true);
    try {
      const toDetach: number[] = [];
      const toAttach = accounts
        .filter((account) => {
          const item = draft[account.id];
          if (!item?.selected) {
            if (attachedById.has(account.id)) toDetach.push(account.id);
            return false;
          }
          return true;
        })
        .map((account) => ({
          accountId: account.id,
          role: draft[account.id]?.role ?? null,
        }));

      await Promise.all(toDetach.map((accountId) => api.detachAccount(cid, accountId)));
      await api.attachAccounts(cid, toAttach);
      await reloadAttached();
      haptic('success');
    } catch {
      haptic('warning');
    } finally {
      setSaveBusy(false);
    }
  };

  return (
    <div className="space-y-4 pb-24">
      <Topbar
        title="Аккаунты кампании"
        subtitle={`выбрано ${selectedCount} из ${accounts?.length ?? 0}`}
      />

      {loading ? (
        <div className="space-y-2">
          <div className="skeleton h-16" />
          <div className="skeleton h-16" />
        </div>
      ) : null}

      <ul className="space-y-2">
        {(accounts ?? []).map((account) => {
          const item = draft[account.id] ?? { selected: false, role: null };
          const isAttached = item.selected;

          return (
            <li
              key={account.id}
              onClick={() => toggleSelection(account.id)}
              className={`card cursor-pointer p-3 transition active:scale-[0.99] ${
                isAttached ? 'border-good/50 ring-1 ring-good/30' : ''
              } ${saveBusy ? 'opacity-70' : ''}`}
            >
              <div className="flex items-center gap-3">
                <Avatar name={account.name || account.username || `acc${account.id}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold text-ink-50">
                      {account.name || account.username || `Account ${account.id}`}
                    </span>
                    <StatusPill status={account.status} />
                    {isAttached ? (
                      <span className="pill pill-good shrink-0 text-[10px]">выбран</span>
                    ) : null}
                  </div>
                  <div className="truncate text-2xs text-ink-500">
                    {account.username && `@${account.username.replace(/^@/, '')}`}
                    {account.proxy && ` · ${account.proxy.ip}:${account.proxy.port}`}
                    {account.banCount ? ` · банов: ${account.banCount}` : ''}
                  </div>
                </div>
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-md border-2 transition ${
                    isAttached
                      ? 'border-good bg-good text-white'
                      : 'border-ink-700 text-transparent'
                  }`}
                  aria-label={isAttached ? 'убрать из кампании' : 'добавить в кампанию'}
                >
                  <Icon name="check" size={14} />
                </span>
              </div>

              {isAttached ? (
                <div
                  className="mt-3 flex flex-wrap gap-2"
                  onClick={(event) => event.stopPropagation()}
                >
                  {ROLE_OPTIONS.map((option) => {
                    const active = item.role === option.value;
                    return (
                      <button
                        key={option.label}
                        type="button"
                        className={`rounded-2xl border px-3 py-1.5 text-xs font-medium transition ${
                          active
                            ? 'border-accent-300 bg-accent-300/15 text-accent-100'
                            : 'border-ink-700 bg-ink-900/70 text-ink-300'
                        }`}
                        onClick={() => setRole(account.id, option.value)}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {!loading && (accounts ?? []).length === 0 ? (
        <div className="text-center text-sm text-ink-500">
          Пока нет аккаунтов. Добавь через раздел «Аккаунты».
        </div>
      ) : null}

      <div className="fixed inset-x-0 bottom-0 z-20 mx-auto w-full max-w-[var(--app-max-width)] px-4 pb-[calc(env(safe-area-inset-bottom)+16px)]">
        <div className="card flex items-center gap-3 p-3">
          <button
            type="button"
            className="btn-ghost flex-1"
            onClick={resetDraft}
            disabled={!hasChanges || saveBusy}
          >
            Сбросить
          </button>
          <button
            type="button"
            className="btn-primary flex-1"
            onClick={save}
            disabled={!hasChanges || saveBusy}
          >
            <Icon name="check" size={16} />
            {saveBusy ? 'Сохраняю…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
}
